import { z } from "zod";
import { ApiError, apiClient } from "./api";

// A tag in the shared vocabulary. Mirrors the backend `TagView`
// (id/name/color); `color` is a `#RRGGBB` string or null.
export const tagSchema = z.object({
  id: z.guid(),
  name: z.string(),
  color: z.string().nullable(),
});
export type Tag = z.infer<typeof tagSchema>;

export const tagListSchema = z.array(tagSchema);

// ---- Vocabulary CRUD ----------------------------------------------

export async function listTags(): Promise<Tag[]> {
  return apiClient.get("/tags", tagListSchema);
}

export async function createTag(input: { name: string; color?: string | null }): Promise<Tag> {
  return apiClient.post("/tags", tagSchema, {
    name: input.name,
    color: input.color ?? null,
  });
}

export async function updateTag(
  id: string,
  input: { name?: string; color?: string | null },
): Promise<Tag> {
  return apiClient.patch(`/tags/${id}`, tagSchema, input);
}

export async function deleteTag(id: string): Promise<void> {
  await apiClient.delete(`/tags/${id}`, z.unknown());
}

// ---- Assignment to a knowledge item -------------------------------

export async function getSourceTags(sourceId: string): Promise<Tag[]> {
  return apiClient.get(`/sources/${sourceId}/tags`, tagListSchema);
}

/** Replace the full tag set on a source (idempotent). Returns the new set. */
export async function setSourceTags(sourceId: string, tagIds: string[]): Promise<Tag[]> {
  return apiClient.put(`/sources/${sourceId}/tags`, tagListSchema, { tag_ids: tagIds });
}

export async function unassignSourceTag(sourceId: string, tagId: string): Promise<void> {
  await apiClient.delete(`/sources/${sourceId}/tags/${tagId}`, z.unknown());
}

// ---- Friendly error mapping ---------------------------------------

/** Maps the common tag-create failures to a UI message; returns null
 * when the caller should surface the error verbatim. */
export function describeCreateTagError(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status === 409) return "ya existe un tag con ese nombre";
  if (error.status === 403) return "no tenés permiso para crear tags";
  if (error.status === 422) return "nombre o color inválido";
  return null;
}
